import { jsx as _jsx } from "react/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { PtsDocumentModal } from 'libs.consent.react';
import { useFeatureFlags } from 'libs.flags.react';
import { SITE_FEATURE_FLAGS } from '../../constants/feature_flags.constants';
import { getApiErrorMessage, getApiInstance } from '../../helpers/api.helpers';
import { useAxonAccountContext } from '../../hooks/use_axon_account_context';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useOrgContext } from '../../hooks/use_org_context';
import { useSiteContext } from '../../hooks/use_site_context';
export const usePtsDocumentAction = (participant) => {
    const { environment } = useCurrentEnvironment();
    const org = useOrgContext();
    const site = useSiteContext();
    const account = useAxonAccountContext();
    const queryClient = useQueryClient();
    const { isFeatureEnabled } = useFeatureFlags();
    const [selectedDocument, setSelectedDocument] = useState(null);
    const isPtsInteractionEnabled = isFeatureEnabled(SITE_FEATURE_FLAGS.DOC_GEN_ECONSENT);
    const apiClient = useMemo(() => (environment ? getApiInstance(environment) : undefined), [environment]);
    // Memoized so the consuming column `useMemo` is not invalidated every render.
    const openPtsDocument = useCallback((document) => {
        if (!isPtsInteractionEnabled) {
            return;
        }
        setSelectedDocument(document);
    }, [isPtsInteractionEnabled]);
    const closePtsDocument = useCallback(() => setSelectedDocument(null), []);
    const invalidateParticipantDocuments = () => {
        if (participant?._id) {
            void queryClient.invalidateQueries({ queryKey: ['participantDocuments', participant._id] });
        }
    };
    // Portal to document.body so the modal escapes the surrounding TabPanel, matching CreateConsentModal.
    const ptsDocumentModal = isPtsInteractionEnabled && selectedDocument
        ? createPortal(_jsx(PtsDocumentModal, { isOpen: true, onClose: closePtsDocument, signedDocId: selectedDocument._id, status: selectedDocument.ec__status, signatureDate: selectedDocument.ec__pts_signature_date_wallclock, finalDocumentUrl: selectedDocument.ec__final_document?.url, accountId: account?._id, isNoPiiSite: org?.c_no_pii === true || site?.c_no_pii === true, apiClient: apiClient, getApiErrorMessage: getApiErrorMessage, onComplete: () => {
                invalidateParticipantDocuments();
                closePtsDocument();
            } }), document.body)
        : null;
    return { isPtsInteractionEnabled, openPtsDocument, ptsDocumentModal };
};
